import {useState, useEffect} from 'react';
import {type FishTypePost, type FishTypeResponse} from 'client';

import {inputMenuActionType, methodType, SWRKeyEnum} from 'interfaces';

import backendCommunicator from 'services/backendCommunicator';
import useIsMounted from 'hooks/useIsMounted';
import {useInputMenuContext} from 'context/InputMenu';

import {type AxiosError} from 'axios';
import useParseURL from 'hooks/useParseURL';

import Alert from 'helpers/Alert';
import {useGetPlanFishTypes} from 'hooks/swr';
import {mutate} from 'swr';

function useAddEditFishType() {
  const {planId} = useParseURL();

  const [createUpdateFishType, setCreateUpdateFishType] = useState<boolean>(false);
  const {data: fishTypes} = useGetPlanFishTypes({resourceId: planId});

  const inputMenuContext = useInputMenuContext();
  const inputMenuState = inputMenuContext?.state;
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const isMounted = useIsMounted();
  const fishTypeId = inputMenuState?.formData && inputMenuState?.formData.id;
  const method = inputMenuState?.method;

  const [fishType, setFishType] = useState<FishTypePost>({
    name: '',
    growthModelId: '',
    planId,
    feedConversionRatioModelId: '',
  });
  const initialFishType = (fishTypes: FishTypeResponse[]): FishTypePost => {
    const fishType = fishTypes.find((fishType) => fishType.id === fishTypeId);
    return {
      name: fishType?.name ?? '',
      growthModelId: fishType?.growthModel.id ?? '',
      planId,
      feedConversionRatioModelId: fishType?.feedConversionRatioModel.id ?? '',
    };
  };

  useEffect(() => {
    fishTypes && setFishType(initialFishType(fishTypes));
  }, [fishTypes]);

  const backendRequest = async () => {
    if (method === methodType.edit && fishTypeId) {
      return await backendCommunicator.updateFishType(fishTypeId, {
        name: fishType.name,
        growthModelId: fishType.growthModelId,
        feedConversionRatioModelId: fishType.feedConversionRatioModelId,
        planId,
      });
    } else {
      return await backendCommunicator.addFishType(fishType);
    }
  };

  useEffect(() => {
    if (createUpdateFishType && fishType.name) {
      backendRequest()
        .then(() => {
          if (isMounted.current) {
            void mutate([planId, SWRKeyEnum.GET_PLAN_FISH_TYPES]);
            void mutate([planId, SWRKeyEnum.GET_PLAN_FISH_TYPES_GROWTH_MODELS]);
            void mutate([planId, SWRKeyEnum.GET_CAGES_WITH_UNALLOCATED_BIOMASS]);
            inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
          }
        })
        .catch((error: AxiosError) => {
          isMounted.current && Alert.error(error);
        })
        .finally(() => {
          isMounted.current && setCreateUpdateFishType(false);
        });
    }
  }, [createUpdateFishType]);

  return {fishType, setFishType, setCreateUpdateFishType, createUpdateFishType};
}
export default useAddEditFishType;
